if (!customElements.get('custom-select')) {
  class CustomSelect extends HTMLElement {
    constructor() {
      super();
      this._initialized = false;
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.selectEl = this.querySelector('select');
      this.btnLabel = this.dataset.sortLabel || 'Sort by';

      const hasLiquidMarkup = Boolean(
        this.querySelector('.custom-select__wrapper')
      );

      if (!hasLiquidMarkup) {
        this.createCustomSelect();
      } else {
        const selectedOption = this.selectEl?.querySelector(
          'option[selected="selected"]'
        );
        if (selectedOption) {
          this.dataset.value = selectedOption.value;
        }
      }

      this.dropdown = this.querySelector('.js-dropdown');
      this.btnToggleDropdown = this.querySelector('.js-btn-dropdown');
      this.listOptions = this.querySelector('.js-list-options');
      this.isExpanded = false;
      this.repositionHandler = () => {
        if (this.isExpanded) this.positionDropdown();
      };
      this.cleanupTimeout = null;

      this.setBtnWidth();

      window.addEventListener('DOMContentLoaded', () => {
        this.setBtnWidth();
      });

      window.addEventListener('shopify:section:load', () => {
        clearTimeout(this.loadTimeout);

        this.loadTimeout = setTimeout(() => {
          this.setBtnWidth();
        }, 300);
      });

      window.addEventListener('resize', () => {
        if (DeviceDetector.isMobile()) {
          return;
        }
        this.setBtnWidth();
      });

      window.addEventListener('resize', this.repositionHandler);
      window.addEventListener('scroll', this.repositionHandler, { passive: true });

      if (!this.btnToggleDropdown || !this.dropdown || !this.listOptions) {
        return;
      }

      this.btnToggleDropdown.textContent = this.btnLabel;

      this.btnToggleDropdown.addEventListener('click', e => {
        e.preventDefault();

        const isExpanded = this.classList.contains('is-expanded');

        if (isExpanded) {
          this.hideDropdown();

          return;
        }

        this.showDropdown();
      });

      this.btnToggleDropdown.addEventListener('keydown', e => {
        if (e.key.includes('Arrow')) {
          e.preventDefault();

          const isArrowRight = e.key.includes('Right');
          const isHorizontalArrow =
            e.key.includes('Left') || isArrowRight;

          if (this.isExpanded && isHorizontalArrow) {
            return;
          }

          const isNextKey = isArrowRight || e.key.includes('Down');

          const btnOptionSelected = this.listOptions.querySelector(
            '.js-btn-option.is-selected'
          );
          const selectedOptionParent =
            btnOptionSelected.parentElement;
          let prevNextBtnOption = isNextKey
            ? selectedOptionParent.nextElementSibling
                ?.firstElementChild
            : selectedOptionParent.previousElementSibling
                ?.firstElementChild;

          if (!prevNextBtnOption) {
            prevNextBtnOption = isNextKey
              ? this.listOptions.firstElementChild.firstElementChild
              : this.listOptions.lastElementChild.firstElementChild;
          }

          this.changeSelectedOption(
            btnOptionSelected,
            prevNextBtnOption
          );

          return;
        }

        if (!this.isExpanded) {
          return;
        }

        const isEscape = e.key === 'Escape';
        const isTab = e.key === 'Tab';

        if (!isEscape && !isTab) {
          return;
        }

        e.preventDefault();

        this.hideDropdown();
      });

      this.listOptions.addEventListener('click', e => {
        const isBtnOption =
          e.target.classList.contains('js-btn-option');

        if (!isBtnOption) {
          return;
        }

        e.preventDefault();

        const btnOption = e.target;
        const isSelected =
          btnOption.classList.contains('is-selected');

        this.hideDropdown();

        if (isSelected) {
          return;
        }

        this.changeSelectedOption(undefined, btnOption);
      });

      document.addEventListener('click', e => {
        if (!this.isExpanded) {
          return;
        }

        const isThisSelect =
          e.target.closest('custom-select') === this;

        if (isThisSelect) {
          return;
        }

        this.hideDropdown();
      });
    }

    setBtnWidth() {
      this.btnToggleDropdown.style.width = '';
    }

    createCustomSelect() {
      const options = this.querySelectorAll('option');
      const selectedOption = this.selectEl.querySelector(
        'option[selected="selected"]'
      );
      let optionsMarkup = '';

      options.forEach(option => {
        const optionMarkup = `
          <li class="custom-select__item">
            <button
              type="button"
              class="custom-select__option button-reset js-btn-option ${
                option.selected ? 'is-selected' : ''
              }"
              data-value="${option.value}"
              tabindex="-1"
              aria-selected="${option.selected}"
            >${option.textContent}</button>
          </li>
        `;

        optionsMarkup += optionMarkup;
      });
      const markup = `
        <div class="custom-select__wrapper">
          <button
            type="button"
            class="custom-select__btn button-reset focus-inset js-btn-dropdown"
            aria-controls="${this.dataset.dropdownId}"
            aria-expanded="false"
          >${this.btnLabel}</button>
          <div class="custom-select__dropdown js-dropdown" id="${this.dataset.dropdownId}">
            <ul class="custom-select__items list-unstyled js-list-options">
              ${optionsMarkup}
            </ul>
          </div>
        </div>
      `;

      this.insertAdjacentHTML('afterbegin', markup);
      this.dataset.value = selectedOption.value;
    }

    changeSelectedOption(
      selectedOption = this.listOptions.querySelector(
        '.js-btn-option.is-selected'
      ),
      newSelectedOption
    ) {
      this.btnToggleDropdown.textContent = this.btnLabel;
      this.dataset.value = newSelectedOption.dataset.value;

      newSelectedOption.classList.add('is-selected');
      newSelectedOption.setAttribute('aria-selected', true);

      selectedOption.classList.remove('is-selected');
      selectedOption.setAttribute('aria-selected', false);

      const nativeSelectedOption = this.selectEl.querySelector(
        'option[selected="selected"]'
      );
      const newNativeSelectedOption = this.selectEl.querySelector(
        `option[value="${this.dataset.value}"]`
      );

      nativeSelectedOption.removeAttribute('selected');
      newNativeSelectedOption.setAttribute('selected', 'selected');

      // Trigger input event

      const inputEvent = new Event('input', { bubbles: true });

      this.selectEl.dispatchEvent(inputEvent);
    }

    positionDropdown() {
      const padding = 12;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const wrapper = this.querySelector('.custom-select__wrapper');
      if (!wrapper) return;

      if (this.cleanupTimeout) {
        clearTimeout(this.cleanupTimeout);
        this.cleanupTimeout = null;
      }

      this.dropdown.style.transform = '';
      this.dropdown.style.maxHeight = '';
      this.dropdown.style.overflowY = '';
      this.dropdown.style.maxWidth = '';
      this.classList.remove('is-dropup');

      requestAnimationFrame(() => {
        const wrapperRect = wrapper.getBoundingClientRect();
        const availableBelow = vh - wrapperRect.bottom - padding;
        const availableAbove = wrapperRect.top - padding;

        if (isMobile) {
          const dropdownRect = this.dropdown.getBoundingClientRect();
          if (availableBelow > 0 && dropdownRect.height > availableBelow) {
            this.dropdown.style.maxHeight = `${Math.floor(availableBelow)}px`;
            this.dropdown.style.overflowY = 'auto';
          }
          this.dropdown.style.transform = '';
          this.dropdown.style.maxWidth = '';
          this.classList.remove('is-dropup');
          return;
        }

        let dropdownRect = this.dropdown.getBoundingClientRect();
        const shouldFlip =
          dropdownRect.bottom > vh - padding && availableAbove > availableBelow;

        if (shouldFlip) {
          this.classList.add('is-dropup');
          dropdownRect = this.dropdown.getBoundingClientRect();
        }

        const space = this.classList.contains('is-dropup')
          ? availableAbove
          : availableBelow;

        if (space > 0 && dropdownRect.height > space) {
          this.dropdown.style.maxHeight = `${Math.floor(space)}px`;
          this.dropdown.style.overflowY = 'auto';
        }

        const maxWidth = Math.max(160, vw - padding * 2);
        this.dropdown.style.maxWidth = `${maxWidth}px`;

        dropdownRect = this.dropdown.getBoundingClientRect();
        const overflowRight = dropdownRect.right - (vw - padding);
        const overflowLeft = padding - dropdownRect.left;

        if (overflowRight > 0) {
          this.dropdown.style.transform = `translateX(-${Math.ceil(
            overflowRight
          )}px)`;
        } else if (overflowLeft > 0) {
          this.dropdown.style.transform = `translateX(${Math.ceil(
            overflowLeft
          )}px)`;
        }
      });
    }

    showDropdown() {
      this.isExpanded = true;
      this.classList.add('is-expanded');
      this.btnToggleDropdown.setAttribute('aria-expanded', true);
      this.positionDropdown();
    }

    hideDropdown() {
      this.isExpanded = false;
      this.classList.remove('is-expanded');
      this.btnToggleDropdown.setAttribute('aria-expanded', false);

      if (this.cleanupTimeout) {
        clearTimeout(this.cleanupTimeout);
        this.cleanupTimeout = null;
      }

      const cleanup = () => {
        if (this.isExpanded) return;
        this.classList.remove('is-dropup');
        this.dropdown.style.transform = '';
        this.dropdown.style.maxHeight = '';
        this.dropdown.style.overflowY = '';
        this.dropdown.style.maxWidth = '';
      };

      const computed = window.getComputedStyle(this.dropdown);
      const durations = String(computed.transitionDuration || '')
        .split(',')
        .map(s => s.trim())
        .map(v => {
          if (!v) return 0;
          if (v.endsWith('ms')) return parseFloat(v) || 0;
          if (v.endsWith('s')) return (parseFloat(v) || 0) * 1000;
          return parseFloat(v) || 0;
        });
      const delays = String(computed.transitionDelay || '')
        .split(',')
        .map(s => s.trim())
        .map(v => {
          if (!v) return 0;
          if (v.endsWith('ms')) return parseFloat(v) || 0;
          if (v.endsWith('s')) return (parseFloat(v) || 0) * 1000;
          return parseFloat(v) || 0;
        });
      const d = Math.max(...durations, 0);
      const dl = Math.max(...delays, 0);
      const cleanupAfter = Math.max(200, Math.round(d + dl + 50));

      const onEnd = e => {
        if (e.target !== this.dropdown) return;
        if (this.isExpanded) return;
        if (e.propertyName !== 'opacity') return;
        this.dropdown.removeEventListener('transitionend', onEnd);
        cleanup();
      };
      this.dropdown.addEventListener('transitionend', onEnd);

      this.cleanupTimeout = setTimeout(() => {
        this.dropdown.removeEventListener('transitionend', onEnd);
        cleanup();
        this.cleanupTimeout = null;
      }, cleanupAfter);
    }
  }

  customElements.define('custom-select', CustomSelect);
}
